import type Stripe from 'stripe';

/**
 * Resolves the base plan item from a Stripe subscription.
 *
 * A subscription may carry multiple items — e.g. a metered SMS item
 * alongside the seat-based base plan. The base plan is the licensed
 * (non-metered) recurring item. Stripe only populates the deprecated top-level
 * `subscription.plan` for single-item subscriptions, so the base item must be
 * resolved from `items.data` instead. Returns `undefined` when no base plan can
 * be identified.
 */
export const getBasePlanItemFromStripeSubscription = (
  subscription: Stripe.Subscription
): Stripe.SubscriptionItem | undefined => {
  const licensedItem = subscription.items.data.find(
    (item) => item.price.recurring?.usage_type === 'licensed'
  );
  if (licensedItem) {
    return licensedItem;
  }

  // Fall back to the sole item for legacy single-item subscriptions whose price
  // predates explicit usage types.
  return subscription.items.data.length === 1
    ? subscription.items.data[0]
    : undefined;
};

export const getProductCostsFromStripePrice = (stripePrice: Stripe.Price) => {
  const isTieredPricing = stripePrice.billing_scheme === 'tiered';

  const basicCost =
    isTieredPricing && stripePrice.tiers && stripePrice.tiers[0]
      ? stripePrice.tiers[0].flat_amount
      : stripePrice.unit_amount;
  const additionalUserSeatCost =
    isTieredPricing && stripePrice.tiers && stripePrice.tiers[1]
      ? stripePrice.tiers[1].unit_amount
      : null;

  return {
    stripeId: stripePrice.id,
    basicCost: basicCost || 0,
    additionalUserSeatCost: additionalUserSeatCost || undefined,
    canBuyAdditionalUserSeats: isTieredPricing && !!additionalUserSeatCost,
  };
};

export const hasUnlimitedUsers = (numberOfUserSeats?: number): boolean =>
  !!numberOfUserSeats && numberOfUserSeats >= 999;
